import {type CSSProperties, Suspense, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {ERROR, UNIT} from '@di-shared/const';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import EnergyPowerRecentGraph from '@di-shared/features/history/energy-usage/EnergyPowerRecentGraph';
import {EnergyUsageRecentProps} from '@di-shared/features/history/energy-usage/EnergyUsageRecent/index';
import {roundToFirstDecimal} from '@di-shared/features/history/energy-usage/utils';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export default function ({vin}: EnergyUsageRecentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: EnergyUsageRecentProps) {
  const ccId = environmentStore.getState().ccid;
  const {data: evRecentData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getRecentEvDriveStats), vin, ccId],
    queryFn: () => diApi.driveHistory.getRecentEvDriveStats({vin, ccId}),
    select: ({data}) => data,
  });

  const isRecentEnergyUsageNone =
    evRecentData.latestDriveDate === '' &&
    evRecentData.powerConsumption === 0 &&
    evRecentData.motorConsumptionRatio === 0 &&
    evRecentData.climateConsumptionRatio === 0 &&
    evRecentData.electricDeviceConsumptionRatio === 0 &&
    evRecentData.batteryManageConsumptionRatio === 0 &&
    evRecentData.avrMileage === 0 &&
    evRecentData.regenPower === 0;
  // No data 화면
  if (isRecentEnergyUsageNone) {
    return <None />;
  }

  // Default 화면
  return <Default vin={vin} />;
}

function None() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  const today = new Date();

  return (
    <section className="section">
      <h2 className="blind">{t('T10002')}</h2>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10184')}
        </h3>
        <span className="section_heading_caption">
          {t('T30638')}:&nbsp;{CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div className="device_info_item">
            <h4 className="device_info_key body_medium_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {0}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('T10078')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('T10079')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('T10080')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('T10081')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <BarChartNone />
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10037')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{0}</span>
                <span className="device_info_unit">{defaultEnergyEfficiencySymbol}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10072')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{0}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Default({vin}: EnergyUsageRecentProps) {
  const ccId = environmentStore.getState().ccid;
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {data: evRecentData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getRecentEvDriveStats), vin, ccId],
    queryFn: () => diApi.driveHistory.getRecentEvDriveStats({vin, ccId}),
    select: ({data}) => data,
  });
  const {convertEnergyEfficiencySymbol, convertEnergyEfficiencyValue} = useUnitConverter();

  const verifiedRecentDrivingDate = parseDateWithFormat(evRecentData.latestDriveDate);
  if (!verifiedRecentDrivingDate) {
    throw new ExpectedError('latestDriveDate error');
  }

  const averageEnergyEfficiencyValue = convertEnergyEfficiencyValue(evRecentData.avrMileage, {skipFormatting: true});
  const roundedAverageEnergyEfficiencyValue =
    typeof averageEnergyEfficiencyValue === 'number' ? roundToFirstDecimal(averageEnergyEfficiencyValue) : averageEnergyEfficiencyValue;

  return (
    <section className="section">
      <h2 className="blind">{t('T10002')}</h2>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10070')}
        </h3>
        <span className="section_heading_caption">
          {t('T30638')}:&nbsp;{CldrDatetimeFormatter.format(verifiedRecentDrivingDate, 'yMMMd')}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div className="device_info_item">
            <h4 className="device_info_key body_medium_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {formatNumberWithCommas(roundToFirstDecimal(evRecentData.powerConsumption))}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={evRecentData.motorConsumptionRatio}
            electricDeviceRatio={evRecentData.electricDeviceConsumptionRatio}
            climateRatio={evRecentData.climateConsumptionRatio}
            batteryManageRatio={evRecentData.batteryManageConsumptionRatio}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('T10078')}</span>
                <span className="legend_value">{evRecentData.motorConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('T10079')}</span>
                <span className="legend_value">{evRecentData.electricDeviceConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('T10080')}</span>
                <span className="legend_value">{evRecentData.climateConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('T10081')}</span>
                <span className="legend_value">{evRecentData.batteryManageConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <ErrorBoundary fallback={<BarChartError />}>
        <Suspense fallback={<BarChartLoading />}>
          <BarChart vin={vin} />
        </Suspense>
      </ErrorBoundary>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10037')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{formatNumberWithCommas(roundedAverageEnergyEfficiencyValue)}</span>
                <span className="device_info_unit">{convertEnergyEfficiencySymbol(evRecentData.avrMileage, {skipFormatting: true})}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10072')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{formatNumberWithCommas(roundToFirstDecimal(evRecentData.regenPower))}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();

  return (
    <section className="section">
      <h2 className="blind">{t('T10002')}</h2>
      <div className="section_heading type_main">
        <button
          type="button"
          className="btn_monthly body_medium_b skeleton"
          style={{width: '160px', height: '20px'}}
        />
        <h3
          className="section_heading_title skeleton"
          style={{width: '200px', height: '28px'}}
        />
        <span
          className="section_heading_caption skeleton"
          style={{width: '160px', height: '20px'}}
        />
      </div>
      <div className="section_container skeleton">
        <div className="section_container_header type_border">
          <div className="device_info_item">
            <h4 className="device_info_key body_medium_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('T10078')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('T10079')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('T10080')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('T10081')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <BarChartLoading />
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10037')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{ERROR.DASH}</span>
                <span className="device_info_unit">{defaultEnergyEfficiencySymbol}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10072')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{ERROR.DASH}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  const today = new Date();

  return (
    <section className="section">
      <h2 className="blind">{t('T10002')}</h2>
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10070')}
        </h3>
        <span className="section_heading_caption">
          {t('T30638')}:&nbsp;{CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div className="device_info_item">
            <h4 className="device_info_key body_medium_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('T10078')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('T10079')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('T10080')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li className="graph_legend_item">
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('T10081')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <BarChartError />
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10037')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{ERROR.DASH}</span>
                <span className="device_info_unit">{defaultEnergyEfficiencySymbol}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10072')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{ERROR.DASH}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

interface RecentEvTripHistoryBase {
  ignOnTime: string;
}

interface RecentEvHourlyChartData<T extends RecentEvTripHistoryBase> {
  hour: number;
  value: number;
  tripHistories: Array<T>;
}

interface RecentEvChartScale {
  maxValue: number;
  chartMaxValue: number;
  guideLineValues: [number, number];
}

function getRecentEvHourlyChartData<T extends RecentEvTripHistoryBase>({
  tripHistories,
  getValue,
}: {
  tripHistories: Array<T>;
  getValue?: (tripHistory: T) => number;
}): Array<RecentEvHourlyChartData<T>> {
  const chartData = Array.from({length: 24}, (_, hour) => ({
    hour,
    value: 0,
    tripHistories: [] as Array<T>,
  }));

  tripHistories.forEach((tripHistory) => {
    const parsedIgnOnTime = parseDateWithFormat(tripHistory.ignOnTime);
    if (!parsedIgnOnTime) {
      return;
    }

    const hour = parsedIgnOnTime.getHours();
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
      return;
    }

    const chartBucket = chartData[hour];
    chartBucket.tripHistories.push(tripHistory);

    if (!getValue) {
      return;
    }

    const nextValue = getValue(tripHistory);
    chartBucket.value += Number.isFinite(nextValue) ? nextValue : 0;
  });

  return chartData;
}

const DEFAULT_MAX_Y_AXIS_VALUE = 10;
function getRecentEvChartScale({values}: {values: Array<number>}): RecentEvChartScale {
  const maxValue = values.reduce((accumulator, value) => {
    if (!Number.isFinite(value)) {
      return accumulator;
    }

    return Math.max(accumulator, value, DEFAULT_MAX_Y_AXIS_VALUE);
  }, 0);

  const chartMaxValue = Math.ceil(maxValue / 10) * 10;

  return {
    maxValue,
    chartMaxValue,
    guideLineValues: [chartMaxValue / 2, chartMaxValue],
  };
}

function BarChart({vin}: {vin: string}) {
  const ccId = environmentStore.getState().ccid;
  const {data: evRecentData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getRecentEvDriveStats), vin, ccId],
    queryFn: () => diApi.driveHistory.getRecentEvDriveStats({vin, ccId}),
    select: ({data}) => data,
  });

  const validLatestDriveDate = parseDateWithFormat(evRecentData.latestDriveDate);
  if (!validLatestDriveDate) {
    throw new ExpectedError('Invalid latestDriveDate');
  }

  const params = {
    vin,
    ccId,
    date: getYYYYMMddFormat(validLatestDriveDate),
    page: 1,
    size: 1000,
  };

  const {data: dailyTripHistory} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getTripHistoryByDate), params],
    queryFn: () => diApi.driveHistory.getTripHistoryByDate(params),
    select: ({data}) => data,
  });

  const chartData = getRecentEvHourlyChartData({
    tripHistories: dailyTripHistory.content,
    getValue: (tripHistory) => tripHistory.evEnergyUsage.totalPowerConsumption / 1000,
  }).map(({hour, value}) => ({
    hour,
    value: roundToFirstDecimal(value),
  }));

  return (
    <BarChartView
      data={chartData}
      scale={getRecentEvChartScale({
        values: chartData.map(({value}) => value),
      })}
      unit={UNIT.KWH}
    />
  );
}

interface RecentEvChartBarData {
  hour: number;
  value: number;
}

interface ChartProps {
  data: Array<RecentEvChartBarData>;
  scale: RecentEvChartScale;
  unit: string;
  fallbackText?: string;
  skeleton?: boolean;
}

interface RecentEvChartTooltipPosition {
  top: number;
  left: number;
  arrowLeft: number;
}

const RECENT_EV_CHART = {
  barCount: 24,
  barWidth: 6,
  barAreaHeight: 116,
  barSidePadding: 8,
  tooltipGap: 9,
  tooltipArrowPadding: 4,
} as const;

const RECENT_EV_CHART_FALLBACK_DATA: Array<RecentEvChartBarData> = Array.from({length: RECENT_EV_CHART.barCount}, (_, hour) => ({
  hour,
  value: 0,
}));

const RECENT_EV_CHART_FALLBACK_SCALE: RecentEvChartScale = {
  maxValue: 10,
  chartMaxValue: 10,
  guideLineValues: [5, 10],
};

function getRecentEvChartRatio(value: number, chartMaxValue: number) {
  if (!Number.isFinite(value) || value <= 0 || !Number.isFinite(chartMaxValue) || chartMaxValue <= 0) {
    return 0;
  }

  return Math.min(value / chartMaxValue, 1);
}

function getRecentEvChartTop(value: number, chartMaxValue: number) {
  return `${Math.round((1 - getRecentEvChartRatio(value, chartMaxValue)) * RECENT_EV_CHART.barAreaHeight)}px`;
}

function getRecentEvChartHourLabel(hour: number) {
  return `${String(hour).padStart(2, '0')}:00`;
}

function getRecentEvChartLeft(hour: number) {
  return `calc(${RECENT_EV_CHART.barSidePadding}px + ((100% - ${RECENT_EV_CHART.barSidePadding * 2 + RECENT_EV_CHART.barWidth}px) / ${
    RECENT_EV_CHART.barCount - 1
  }) * ${hour})`;
}

function getRecentEvChartDefaultSelectedHour(data: Array<RecentEvChartBarData>) {
  let nextSelectedHour: number | null = null;
  let maxValue = 0;

  for (const {hour, value} of data) {
    if (value > maxValue) {
      maxValue = value;
      nextSelectedHour = hour;
    }
  }

  return maxValue > 0 ? nextSelectedHour : null;
}

function BarChartView({data, scale, unit, fallbackText, skeleton = false}: ChartProps) {
  const {t} = useInferableTranslation();
  const isFallback = fallbackText !== undefined;
  const safeChartMaxValue = scale.chartMaxValue > 0 ? scale.chartMaxValue : 1;
  const chartDataKey = data.map(({hour, value}) => `${hour}:${value}`).join('|');
  const defaultSelectedHour = getRecentEvChartDefaultSelectedHour(data);
  const barThinRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [selectedHour, setSelectedHour] = useState<number | null>(() => defaultSelectedHour);
  const [tooltipPosition, setTooltipPosition] = useState<RecentEvChartTooltipPosition | null>(null);

  useEffect(() => {
    setSelectedHour(defaultSelectedHour);
  }, [chartDataKey, defaultSelectedHour]);

  const positiveValues = data.map(({value}) => value).filter((value) => Number.isFinite(value) && value > 0);
  const maxValue = positiveValues.length ? Math.max(...positiveValues) : 0;
  const minValue = positiveValues.length ? Math.min(...positiveValues) : 0;
  const selectedData = isFallback || selectedHour === null ? undefined : data.find(({hour}) => hour === selectedHour);
  const topGuideLineTop = getRecentEvChartTop(scale.guideLineValues[1], safeChartMaxValue);
  const midGuideLineTop = getRecentEvChartTop(scale.guideLineValues[0], safeChartMaxValue);
  const topGuideLineLabel = formatNumberWithCommas(roundToFirstDecimal(scale.guideLineValues[1]));
  const midGuideLineLabel = formatNumberWithCommas(roundToFirstDecimal(scale.guideLineValues[0]));
  const maxValueLabel = fallbackText ?? formatNumberWithCommas(maxValue);
  const minValueLabel = fallbackText ?? formatNumberWithCommas(minValue);

  useLayoutEffect(() => {
    if (!selectedData || !barThinRef.current || !tooltipRef.current) {
      setTooltipPosition(null);
      return;
    }

    const activeSelectedData = selectedData;
    const barThinElement = barThinRef.current;
    const tooltipElement = tooltipRef.current;

    function updateTooltipPosition() {
      const selectedBar = barThinElement.querySelector<HTMLElement>(`[data-hour="${activeSelectedData.hour}"]`);
      const chartMain = barThinElement.querySelector<HTMLElement>('.bar_thin_main');
      if (!selectedBar || !chartMain) {
        setTooltipPosition(null);
        return;
      }

      const containerRect = barThinElement.getBoundingClientRect();
      const chartMainRect = chartMain.getBoundingClientRect();
      const selectedBarTarget = selectedBar.querySelector<HTMLElement>('.bar_thin_fill') ?? selectedBar;
      const selectedBarRect = selectedBarTarget.getBoundingClientRect();
      const tooltipWidth = tooltipElement.offsetWidth;
      const tooltipHeight = tooltipElement.offsetHeight;
      const barCenter = selectedBarRect.left - containerRect.left + selectedBarRect.width / 2;
      const chartMainLeft = chartMainRect.left - containerRect.left;
      const chartMainRight = chartMainRect.right - containerRect.left;
      const minTooltipCenter = chartMainLeft + tooltipWidth / 2;
      const maxTooltipCenter = chartMainRight - tooltipWidth / 2;
      const fallbackTooltipCenter = chartMainLeft + chartMainRect.width / 2;
      const clampedTooltipCenter = Math.min(Math.max(barCenter, minTooltipCenter), maxTooltipCenter);
      const tooltipLeft = minTooltipCenter <= maxTooltipCenter ? clampedTooltipCenter : fallbackTooltipCenter;
      const tooltipTop = selectedBarRect.top - containerRect.top - tooltipHeight - RECENT_EV_CHART.tooltipGap;
      const unclampedArrowLeft = barCenter - (tooltipLeft - tooltipWidth / 2);
      const arrowLeft = Math.min(
        Math.max(unclampedArrowLeft, RECENT_EV_CHART.tooltipArrowPadding),
        tooltipWidth - RECENT_EV_CHART.tooltipArrowPadding
      );

      setTooltipPosition({
        top: tooltipTop,
        left: tooltipLeft,
        arrowLeft,
      });
    }

    updateTooltipPosition();
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [chartDataKey, selectedData, unit]);

  const tooltipStyle = {
    display: 'inline-flex',
    top: `${tooltipPosition?.top ?? 0}px`,
    left: `${tooltipPosition?.left ?? 0}px`,
    visibility: tooltipPosition ? 'visible' : 'hidden',
    '--ev-arrow-left': `${tooltipPosition?.arrowLeft ?? 0}px`,
  } as CSSProperties;

  return (
    <div className={clsx('section_container', {skeleton})}>
      <div
        ref={barThinRef}
        className="bar_thin"
        data-tooltip-mode="hour-range"
      >
        <div className="bar_thin_y_axis">
          <span
            className="bar_thin_y_label"
            style={{top: topGuideLineTop}}
          >
            {topGuideLineLabel}
          </span>
          <span
            className="bar_thin_y_label"
            style={{top: midGuideLineTop}}
          >
            {midGuideLineLabel}
          </span>
        </div>
        <div className="bar_thin_main">
          <span
            className="bar_thin_line"
            style={{top: topGuideLineTop}}
            aria-hidden="true"
          />
          <span
            className="bar_thin_line"
            style={{top: midGuideLineTop}}
            aria-hidden="true"
          />
          <div className="bar_thin_bars">
            {data.map(({hour, value}) => {
              const isSelected = hour === selectedHour;
              const hourLabel = getRecentEvChartHourLabel(hour);
              const nextHourLabel = getRecentEvChartHourLabel(hour + 1);

              return (
                <button
                  key={hour}
                  type="button"
                  className={`bar_thin_item${isSelected ? ' is-active' : ''}`}
                  data-hour={hour}
                  data-date={hourLabel}
                  data-value={value}
                  aria-label={`${hourLabel}-${nextHourLabel} ${formatNumberWithCommas(value)}${unit}`}
                  aria-pressed={isSelected}
                  disabled={isFallback}
                  tabIndex={isFallback ? -1 : 0}
                  onClick={() => {
                    if (!isFallback) {
                      setSelectedHour(hour);
                    }
                  }}
                  style={{
                    left: getRecentEvChartLeft(hour),
                    width: `${RECENT_EV_CHART.barWidth}px`,
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                  }}
                >
                  <span
                    className="bar_thin_fill"
                    style={{
                      height: `${Math.round(getRecentEvChartRatio(value, safeChartMaxValue) * RECENT_EV_CHART.barAreaHeight)}px`,
                    }}
                  />
                </button>
              );
            })}
          </div>
          <div
            className="bar_thin_x_axis"
            aria-hidden="true"
          >
            <span className="bar_thin_x_label">00:00</span>
            <span className="bar_thin_x_label">08:00</span>
            <span className="bar_thin_x_label">16:00</span>
            <span className="bar_thin_x_label">24:00</span>
          </div>
        </div>
        {selectedData && (
          <div
            ref={tooltipRef}
            className="speech_bubble bar_thin_tooltip"
            role="tooltip"
            style={tooltipStyle}
          >
            <span className="time">
              {getRecentEvChartHourLabel(selectedData.hour)}-{getRecentEvChartHourLabel(selectedData.hour + 1)}
            </span>
            <span className="wattage">
              {formatNumberWithCommas(selectedData.value)}
              {unit}
            </span>
          </div>
        )}
      </div>

      <ul className="statistics_info_list">
        <li className="statistics_info_item">
          <span className="statistics_info_key">{t('T10075')}</span>
          <span className="statistics_info_val">
            <span className="statistics_info_number">{maxValueLabel}</span>
            <span className="statistics_info_unit">{unit}</span>
          </span>
        </li>
        <li className="statistics_info_item">
          <span className="statistics_info_key">{t('T10076')}</span>
          <span className="statistics_info_val">
            <span className="statistics_info_number">{minValueLabel}</span>
            <span className="statistics_info_unit">{unit}</span>
          </span>
        </li>
      </ul>
    </div>
  );
}

function BarChartLoading() {
  return (
    <BarChartView
      data={RECENT_EV_CHART_FALLBACK_DATA}
      scale={RECENT_EV_CHART_FALLBACK_SCALE}
      unit={UNIT.KWH}
      fallbackText={ERROR.DASH}
      skeleton
    />
  );
}

function BarChartNone() {
  return (
    <BarChartView
      data={RECENT_EV_CHART_FALLBACK_DATA}
      scale={RECENT_EV_CHART_FALLBACK_SCALE}
      unit={UNIT.KWH}
      fallbackText="0"
    />
  );
}

function BarChartError() {
  return (
    <BarChartView
      data={RECENT_EV_CHART_FALLBACK_DATA}
      scale={RECENT_EV_CHART_FALLBACK_SCALE}
      unit={UNIT.KWH}
      fallbackText={ERROR.DASH}
    />
  );
}
